// ./src/components/features/Submissions/SubmissionStatusChanger/SubmissionStatusChanger.tsx
import React, { useState } from "react";
import { Select, Group, Text, ComboboxItem } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { SUBMISSION_STATUSES, STATUS_CONFIG, ICON_SIZE_MAP } from "@/config";
import { useSubmissions } from "@/hooks";
import { AppIcon } from "@/components/generic";

// ============================================
// Types
// ============================================

export interface SubmissionStatusChangerProps {
  /** Submission ID to update */
  submissionId: string;
  /** Current submission status */
  currentStatus: number | undefined;
  /** Callback when status is successfully changed */
  onStatusChanged?: (newStatus: number) => void;
  /** Size of the select input */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Disable the changer */
  disabled?: boolean;
}

// ============================================
// Status Options
// ============================================

const STATUS_OPTIONS = [
  {
    value: String(SUBMISSION_STATUSES.SPAM),
    label: STATUS_CONFIG[SUBMISSION_STATUSES.SPAM].label,
  },
  {
    value: String(SUBMISSION_STATUSES.RECEIVED),
    label: STATUS_CONFIG[SUBMISSION_STATUSES.RECEIVED].label,
  },
  {
    value: String(SUBMISSION_STATUSES.REVIEWED),
    label: STATUS_CONFIG[SUBMISSION_STATUSES.REVIEWED].label,
  },
  {
    value: String(SUBMISSION_STATUSES.ACCEPTED),
    label: STATUS_CONFIG[SUBMISSION_STATUSES.ACCEPTED].label,
  },
  {
    value: String(SUBMISSION_STATUSES.DECLINED),
    label: STATUS_CONFIG[SUBMISSION_STATUSES.DECLINED].label,
  },
];

// ============================================
// Component
// ============================================

export const SubmissionStatusChanger: React.FC<
  SubmissionStatusChangerProps
> = ({
  submissionId,
  currentStatus,
  onStatusChanged,
  size = "sm",
  disabled = false,
}) => {
  const { update } = useSubmissions({ autoLoad: false });
  const [isUpdating, setIsUpdating] = useState(false);

  const iconSize = ICON_SIZE_MAP[size] ?? 14;

  // Get status config by value
  const getStatusConfig = (value: string) => {
    const statusCode = parseInt(value, 10);
    return (
      STATUS_CONFIG[statusCode] ?? {
        label: "Unknown",
        icon: "HelpCircle",
        color: "neutral",
      }
    );
  };

  // Render option with icon
  const renderOption = ({ option }: { option: ComboboxItem }) => {
    const config = getStatusConfig(option.value);
    return (
      <Group gap="sm" wrap="nowrap">
        <AppIcon
          iconName={config.icon}
          size={iconSize}
          style={{ color: `var(--mantine-color-${config.color}-6)` }}
        />
        <Text size={size}>{option.label}</Text>
      </Group>
    );
  };

  // Handle status change
  const handleStatusChange = async (value: string | null) => {
    if (!value || isUpdating) return;

    const newStatus = parseInt(value, 10);
    if (newStatus === currentStatus) return;

    setIsUpdating(true);

    try {
      const result = await update(submissionId, { status: newStatus });

      if (result) {
        notifications.show({
          title: "Status Updated",
          message: "Submission status has been updated successfully",
          color: "success",
          icon: <AppIcon iconName="Check" size={16} />,
        });
        onStatusChanged?.(newStatus);
      } else {
        notifications.show({
          title: "Update Failed",
          message: "Failed to update submission status. Please try again.",
          color: "error",
          icon: <AppIcon iconName="X" size={16} />,
        });
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "An error occurred while updating status",
        color: "error",
        icon: <AppIcon iconName="X" size={16} />,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Get current status config for left section icon
  const currentConfig = getStatusConfig(
    String(currentStatus ?? SUBMISSION_STATUSES.RECEIVED)
  );

  return (
    <Select
      data={STATUS_OPTIONS}
      value={String(currentStatus ?? SUBMISSION_STATUSES.RECEIVED)}
      onChange={handleStatusChange}
      disabled={disabled || isUpdating}
      size={size}
      placeholder="Select status"
      allowDeselect={false}
      comboboxProps={{ withinPortal: true }}
      renderOption={renderOption}
      leftSection={
        <AppIcon
          iconName={currentConfig.icon}
          size={iconSize}
          style={{ color: `var(--mantine-color-${currentConfig.color}-6)` }}
        />
      }
    />
  );
};
